import { t } from '@lingui/macro';
import { IMapFeatureDataLineString, IMapFeatureDataPoint } from './IMapData';
import { ICorrelatedSite, ICorrelation, ICorrelationListItem, IGraphData, IPath } from './ICorrelation';
import { ILnSDetails } from './ILnSResponse';
import { PaginatedResponse } from '@hwm/ui-lib';
import { IAudioGraphData } from './IAudioGraphData';

export interface SurveyMapModel {
  id: string;
  latitude: number;
  longitude: number;
}

//

export interface SurveysListModel extends PaginatedResponse<SurveyBaseModel> {}

export interface SurveyBaseModel extends SurveyMapModel {
  name: string;
  surveyor: string;
  date: string; // as Date
  duration: string;
  status: string; // as SurveyStatusType but can contain multiple comma-separated values
  result: SurveyResultType;
  numberOfRecordings: number;
  numberOfCorrelations: number;
  coordinatesType: SurveyCoordinatesType;
  numberOfSites: number;
  linkedSurveys: LinkedSurveyModel[] | null;
  isClone: boolean;
}

const SurveyStatus = ['Configuring', 'DevicesConfigured', 'MapLayoutCreated', 'DataCollected', 'Completed'] as const;
export type SurveyStatusType = (typeof SurveyStatus)[number];
export const SurveyStatusOptionsFn = (): Record<SurveyStatusType, string> => ({
  Configuring: t`Configuring`,
  DevicesConfigured: t`Devices Configured`,
  MapLayoutCreated: t`Map Layout Created`,
  DataCollected: t`Data Collected`,
  Completed: t`Completed`,
});

const SurveyResult = ['NotSet', 'LeakDetected', 'NoLeak', 'Inconclusive'] as const;
export type SurveyResultType = (typeof SurveyResult)[number];
export const SurveyResultOptionsFn = (): Record<SurveyResultType, string> => ({
  NotSet: t`Not Set`,
  LeakDetected: t`Leak Detected`,
  NoLeak: t`No Leak`,
  Inconclusive: t`Inconclusive`,
});

const SurveyCoordinates = ['None', 'Basic', 'MapOnly', 'MapAndGis'] as const;
export type SurveyCoordinatesType = (typeof SurveyCoordinates)[number];
export const SurveyCoordinatesOptionsFn = (): Record<SurveyCoordinatesType, string> => ({
  None: t`None`,
  Basic: t`Basic`,
  MapOnly: t`Map Only`,
  MapAndGis: t`Map and GIS`,
});

interface LinkedSurveyModel {
  id: string;
  date: string;
}

//

export interface SurveyDetailsModel extends Omit<SurveyBaseModel, 'isCloned'> {
  devices: SurveyDetailsDeviceModel[];
  mapData: SurveyDetailsMapFeatureDataModel[];
  recordings: SurveyDetailsRecordingModel[];
  latestCorrelations: SurveyDetailsCorrelationListItemModel[];
  lns: SurveyDetailsLnsModel[];
  latestCorrelationSettings: SurveyDetailsCorrelationSettingsModel;
}

export interface SurveyDetailsDeviceModel {
  serialNumber: string;
  loggerType: string;
  recordingSettings: {
    filterNotch: number;
    filterLow: number;
    filterHigh: number;
  };
}

export interface SurveyDetailsPointModel extends Pick<IMapFeatureDataPoint, 'geometry' | 'type'> {
  id: number | null;
  deviceId: number | null;
  siteId: number | null;
  deviceSerial: string | null;
}

export interface SurveyDetailsLineStringModel
  extends Pick<IMapFeatureDataLineString, 'geometry' | 'type' | 'pipeMaterialType' | 'pipeDiameterType'> {
  id: number | null;
  diameterInMillimetres: number | null;
  startId: number | null;
  endId: number | null;
  length: number | null;
  soundVelocity: number | null;
}

export type SurveyDetailsMapFeatureDataModel = SurveyDetailsPointModel | SurveyDetailsLineStringModel;

export interface SurveyDetailsRecordingModel {
  id: string;
  deviceId: string;
  date: string; // as Date
  url: string;
}

export interface SurveyDetailsCorrelationSettingsModel<Type extends 'default' | 'form' = 'default'> {
  onlyCorrelateNeighbours: boolean;
  devicePairSettings: SurveyDetailsCorrelationSettingsDevicePairModel<Type>[];
}

interface SurveyDetailsCorrelationSettingsDevicePairModel<Type extends 'default' | 'form' = 'default'> {
  device1: string;
  device2: string;
  date: string; // as Date
  bandPassMinFilter: number;
  bandPassMaxFilter: number;
  centrePeakSuppression: boolean;
  peaksSuppressed: Type extends 'default' ? number : string;
}

// correlation

export interface SurveyDetailsCorrelationLeakPath {
  leakLatitude: number;
  leakLongitude: number;
  paths: IPath[];
}

export interface SurveyDetailsCorrelationListItemModel
  extends Pick<ICorrelationListItem, 'confidence' | 'confidenceLevel' | 'createDate'>,
    SurveyDetailsCorrelationLeakPath {
  id: string;
  site1: SurveyDetailsCorrelationSiteModel;
  site2: SurveyDetailsCorrelationSiteModel;
  //
  leakDistance1: number;
  leakDistance2: number;
}

export interface SurveyDetailsCorrelationModel
  extends Pick<
      ICorrelation,
      | 'confidence'
      | 'confidenceLevel'
      | 'leakDistance'
      | 'pipeSections'
      | 'unitSystem'
      | 'createDate'
      | 'timeOfRecording'
    >,
    SurveyDetailsCorrelationLeakPath {
  id: string;
  error: number;
  site1: SurveyDetailsCorrelationSiteModel;
  site2: SurveyDetailsCorrelationSiteModel;
  leakDistance2: number;
  recording1Url: string;
  recording2Url: string;
  distance: IGraphData;
  graphs: IGraphData<IAudioGraphData>[]; // coherence
}

export interface SurveyDetailsCorrelationSiteModel extends Pick<ICorrelatedSite, 'address' | 'latitude' | 'longitude'> {
  deviceId: string;
  siteId: string;
}

export interface SurveyDetailsLnsModel extends ILnSDetails {}

// notes

export interface PagedSurveyDetailsNotesModel extends PaginatedResponse<SurveyDetailsNoteModel> {}

export interface SurveyDetailsNoteModel {
  noteId: string;
  time: string; // as Date
  note?: string;
  url?: string;
}

// clone

export interface SurveyCloneFormModel {
  name: string;
  mapData: SurveyCloneFormMapDataModel;
  settings: SurveyDetailsCorrelationSettingsModel<'form'>;
}

export interface SurveyCloneAPIModel {
  surveyName: string;
  mapFeatures: SurveyDetailsMapFeatureDataModel[];
  latestSettings: SurveyDetailsCorrelationSettingsModel;
}

export interface SurveyCloneFormMapDataModel<Type extends 'default' | 'history' = 'default'> {
  devices: Type extends 'history' ? SurveyDetailsPointModel[][] : SurveyDetailsPointModel[];
  pipes: Type extends 'history' ? SurveyDetailsLineStringModel[][] : SurveyDetailsLineStringModel[];
}
